import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Lock, LogIn } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Bem-vindo de volta!");
      navigate("/app/agenda", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3EFE6] flex items-center justify-center px-4 relative grain" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors mb-6" data-testid="login-back-link">
          <ArrowLeft size={15} /> Voltar ao site
        </Link>
        <div className="bg-white border border-stone-200 rounded-2xl shadow-xl p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-8">
            <Logo size={44} />
            <div>
              <p className="font-display text-2xl leading-none">Marcio Pissaia</p>
              <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-stone-500 mt-1">Sistema de Gestão</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="mt-2 w-full rounded-lg border border-stone-300 px-4 py-3 text-sm focus:outline-none focus:border-[#C8A279] focus:ring-2 focus:ring-[#C8A279]/20 transition-all"
              />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500" htmlFor="login-password">Senha</label>
              <input
                id="login-password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 w-full rounded-lg border border-stone-300 px-4 py-3 text-sm focus:outline-none focus:border-[#C8A279] focus:ring-2 focus:ring-[#C8A279]/20 transition-all"
              />
            </div>
            {error && (
              <p data-testid="login-error-message" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#121212] text-white rounded-full py-3.5 text-sm font-medium hover:bg-stone-700 transition-colors duration-300 disabled:opacity-50"
            >
              {busy ? <Lock size={15} className="animate-pulse" /> : <LogIn size={15} />}
              {busy ? "Entrando…" : "Entrar no sistema"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
